<template>
    <driver-panel />
    <div class="mb-3">
        <page-header>
            Dispatch
            <template v-slot:actions>
                <div class="button-list">
                    <div class="btn-group">
                        <v-btn variant="tonal" class="mr-1 bg-bolt text-white">
                            <a
                                href="/index.cfm?module=dispatch&template=frmstdload"
                            >
                                Create from Template
                            </a>
                        </v-btn>
                        <v-btn variant="tonal" class="mr-1 bg-bolt text-white">
                            <a
                                href="/index.cfm?module=dispatch&template=dspLoad"
                            >
                                Create
                            </a>
                        </v-btn>
                        <bolt-router-nav v-if="!isProduction" />
                    </div>
                </div>
            </template>
        </page-header>
    </div>
    <v-card class="overflow-visible border-edge">
        <div class="flex items-center justify-between border-b border-edge">
            <grid-tabs
                :active="activeTab"
                :tabs="views"
                @update:active="onTabChange"
            />
        </div>

        <grid
            storage-key="dispatch"
            :rows="loads"
            :loading="loading"
            :columns="columns"
            :context-menu-items="contextMenuItems"
            @refresh="onRefresh"
        ></grid>
    </v-card>
    <dispatch-actions />
    <auto-refresh-timer @refresh="onRefresh" />
</template>

<script>
import Grid from '@/components/grid/Grid.vue'
import { useLoadsStore } from '@/stores/Loads'
import { useTruckStore } from '@/stores/Trucks'
import { useAgGridStore } from '@/stores/AgGrid'
import { columns, views } from '@/grids/dispatch'
import { useDriverStore } from '@/stores/Drivers'
import { useTerminalStore } from '@/stores/Terminals'
import GridTabs from '@/components/grid/GridTabs.vue'
import contextMenuItems from '@/grids/dispatch/actions'
import BoltRouterNav from '@/components/BoltRouterNav.vue'
import { ALL_ACTIVE_TAB, TABS } from '@/constants/dispatch'
import PageHeader from '@/components/shared/PageHeader.vue'
import AutoRefreshTimer from '@/components/AutoRefreshTimer.vue'
import DriverPanel from '@/components/panels/dispatch/driver/Index.vue'
import DispatchActions from '@/components/actions/dispatch/DispatchActions.vue'

export default {
    /**
     * The component's registered child components.
     *
     * @type {Object}
     */
    components: {
        Grid,
        GridTabs,
        PageHeader,
        DriverPanel,
        BoltRouterNav,
        AutoRefreshTimer,
        DispatchActions,
    },

    /**
     * The component's computed properties.
     *
     * @type {Object}
     */
    computed: {
        /**
         * Get the current active tab/grid view.
         *
         * @return {String}
         */
        activeTab() {
            return this.gridStore.active.view
        },

        /**
         * Get the loads to be displayed in the grid.
         *
         * @return {Array}
         */
        loads() {
            return Object.freeze(this.store.loads.map((load) => ({ ...load })))
        },
    },

    /**
     * The component's local methods.
     *
     * @type {Object}
     */
    methods: {
        /**
         * Add the current view/tab to the URL as a query param.
         *
         * @param {String} view
         * @return {void}
         */
        addViewParamToUrl(view) {
            const query = new URLSearchParams({
                ...this.$route.query,
                view,
            }).toString()

            history.pushState({}, null, `${this.$route.path}?${query}`)
        },

        /**
         * Fetch all data needed for the component.
         *
         * @return {void}
         */
        async fetchData() {
            await Promise.all([
                this.driverStore.get(),
                this.store.fetchStatuses(),
                this.truckStore.get(),
                this.terminalStore.get(),
            ])

            await this.getLoads()
        },

        /**
         * Fetch the active tab's loads from the API.
         *
         * @return {void}
         */
        async getActiveTabLoads() {
            this.loading = true

            try {
                await this.store.getLoadboardWithStatuses([this.activeTab])
            } catch (error) {
                console.log(error)
            } finally {
                this.loading = false
            }
        },

        /**
         * Get the non-active tab's loads from the API.
         *
         * @return {void}
         */
        async getNonActiveTabLoads() {
            const nonActiveTabs = TABS.filter((tab) => tab !== this.activeTab)

            try {
                await this.store.getLoadboardWithStatuses(nonActiveTabs)
            } catch (error) {
                console.log(error)
            }
        },

        /**
         * Fetch all loads from the API.
         *
         * @return {void}
         */
        async getLoads() {
            if (this.activeTab !== ALL_ACTIVE_TAB) {
                await this.getActiveTabLoads()
            }

            await this.getNonActiveTabLoads()
        },

        /**
         * On grid refresh event handler.
         *
         * @return {void}
         */
        async onRefresh() {
            this.getLoads()
        },

        /**
         * Handle the tab/view change event.
         *
         * @param {String} tab
         */
        onTabChange(tab) {
            this.gridStore.setActiveView(tab)

            this.addViewParamToUrl(tab)
        },
    },

    /**
     * The component's name used for debugging.
     *
     * @type {String}
     */
    name: 'DispatchIndex',

    /**
     * The component's created lifecycle hook.
     *
     * @return {void}
     */
    async created() {
        this.gridStore.setViews(views)

        this.onTabChange(this.$route.query.view || 'intransit')

        await this.fetchData()
    },

    /**
     * Get the component's initial state.
     *
     * @return {Object}
     */
    data() {
        return {
            views,
            columns,
            loading: true,
            contextMenuItems,
            isProduction: process.env.NODE_ENV === 'production',
        }
    },

    /**
     * The component's initialization/setup hook.
     *
     * @return {void}
     */
    setup() {
        const store = useLoadsStore()
        const gridStore = useAgGridStore()
        const truckStore = useTruckStore()
        const driverStore = useDriverStore()
        const terminalStore = useTerminalStore()

        return { store, gridStore, truckStore, driverStore, terminalStore }
    },
}
</script>
